import React from 'react'
import { IOtaReservation, IPost } from '../../static/interfaces'
import moment from 'moment'
import { otaReservationService } from '../../core/services'
import { IOtaReservationEditPermissions, ISaveResult } from './types'
export interface IOtaReservationTourEditor {
  selectedPost: IPost | null
  onChangePostIds: (postIds: number[]) => void
  postEditable: boolean
  tourDateEdit: string
  setTourDateEdit: (value: string) => void
  tourStartAtEdit: string
  setTourStartAtEdit: (value: string) => void
  tourDatetimeEditable: boolean
  posts: IPost[]
}
const createTourDraft = (reservation: IOtaReservation, fallbackPost: IPost | null) => ({
  selectedPost: reservation.post ?? fallbackPost ?? null,
  tourDateEdit: moment(reservation.start_at).format('YYYY-MM-DD'),
  tourStartAtEdit: moment(reservation.start_at).format('HH:mm'),
})
export const useTourEditor = (
  otaReservation: IOtaReservation,
  posts: IPost[],
  fallbackPost: IPost | null,
  permissions: IOtaReservationEditPermissions
) => {
  const [draft, setDraft] = React.useState(() => createTourDraft(otaReservation, fallbackPost))

  const fieldPermissions = {
    postEditable: permissions.canUpdateOtaReservation && !otaReservation.is_past,
    tourDatetimeEditable: Boolean(
      permissions.canUpdateOtaReservation && !otaReservation.is_past && otaReservation.jwt_post_id
    ),
  }
  const updateDraft = (updates: Partial<typeof draft>) =>
    setDraft(current => ({ ...current, ...updates }))

  const onChangePostIds = (ids: number[]) =>
    updateDraft({ selectedPost: posts.find(post => post.id === ids[0]) ?? null })

  /**
   * ツアーのポスト変更や催行時間の更新があった場合に保存
   * @returns 更新後のOTA予約情報と、更新APIを実行したかどうか
   */
  const save = async (
    onSaved?: (reservation: IOtaReservation) => void
  ): Promise<ISaveResult<IOtaReservation>> => {
    let updatedOtaReservation = otaReservation
    let saved = false
    const selectedPost = draft.selectedPost
    const postChanged = Boolean(
      fieldPermissions.postEditable &&
        selectedPost &&
        selectedPost.id !== otaReservation.jwt_post_id
    )
    const tourDatetimeChanged =
      draft.tourDateEdit !== moment(otaReservation.start_at).format('YYYY-MM-DD') ||
      draft.tourStartAtEdit !== moment(otaReservation.start_at).format('HH:mm')

    // 新UIでは複数項目をまとめて保存するため、確認も一つのダイアログへ集約する。
    // API呼び出し前に確認することで、キャンセル時に部分更新を残さない。
    const confirmationMessages: string[] = []
    if (postChanged && selectedPost) {
      const productCode = selectedPost.bokun_product_code || '-'
      confirmationMessages.push(
        `ツアーを設定: ${selectedPost.id} | [${productCode}] | ${selectedPost.short_name} | ${selectedPost.name}`
      )
    }
    if (fieldPermissions.tourDatetimeEditable && tourDatetimeChanged) {
      confirmationMessages.push(`ツアー日時を更新: ${draft.tourDateEdit} ${draft.tourStartAtEdit}`)
    }
    if (confirmationMessages.length > 0) {
      const confirmed = window.confirm(
        `以下の変更を保存しますか？\n\n${confirmationMessages.join('\n')}`
      )
      if (!confirmed) {
        return { value: otaReservation, saved: false, canceled: true }
      }
    }

    if (postChanged && selectedPost) {
      const response = await otaReservationService.setPost(otaReservation.id, selectedPost.id)
      updatedOtaReservation = response.updatedOtaReservation as IOtaReservation
      onSaved?.(updatedOtaReservation)
      saved = true
    }
    if (fieldPermissions.tourDatetimeEditable && tourDatetimeChanged) {
      const response = await otaReservationService.setTourDatetime(otaReservation.id, {
        tour_date: draft.tourDateEdit,
        tour_start_at: draft.tourStartAtEdit,
      })
      updatedOtaReservation = response.updatedOtaReservation as IOtaReservation
      onSaved?.(updatedOtaReservation)
      saved = true
    }
    return { value: updatedOtaReservation, saved }
  }

  const reset = (reservation = otaReservation) =>
    setDraft(
      createTourDraft(
        reservation,
        reservation === otaReservation ? fallbackPost : draft.selectedPost ?? fallbackPost
      )
    )
  const tourEdit: IOtaReservationTourEditor = {
    ...draft,
    ...fieldPermissions,
    onChangePostIds,
    setTourDateEdit: tourDateEdit => updateDraft({ tourDateEdit }),
    setTourStartAtEdit: tourStartAtEdit => updateDraft({ tourStartAtEdit }),
    posts,
  }

  return { editor: tourEdit, save, reset }
}
